import React from 'react';
import {
  TWILIGHT_NAVY,
  IRIS_PURPLE,
  IRIS_PURPLE_HOVER,
  SAGE_GREEN,
  LIVING_CORAL,
  DUSTY_BLUE,
  DUSTY_BLUE_DEEP,
  SHELL_WHITE,
  alpha,
} from '../../theme/colors';
import { T } from '../../theme/typography';

const SIZE_STYLES = {
  md: { padding: '12px 24px', fontSize: '16px' },
  sm: { padding: '8px 16px', fontSize: '14px' },
};

// tone -> { background, color, hoverBackground, border }
const TONE_STYLES = {
  purple: {
    background: IRIS_PURPLE,
    color: SHELL_WHITE,
    hoverBackground: IRIS_PURPLE_HOVER,
  },
  green: {
    background: SAGE_GREEN,
    color: SHELL_WHITE,
  },
  gradient: {
    background: `linear-gradient(to right, ${IRIS_PURPLE}, ${LIVING_CORAL})`,
    color: SHELL_WHITE,
  },
  navy: {
    background: 'transparent',
    color: TWILIGHT_NAVY,
    border: `1px solid ${alpha('navy', 0.2)}`,
    hoverBackground: alpha('navy', 0.05),
  },
  white: {
    background: alpha('white', 0.12),
    color: SHELL_WHITE,
    border: `1px solid ${alpha('white', 0.3)}`,
    hoverBackground: alpha('white', 0.2),
  },
  // Dark-surface text links (e.g. AddToHomeScreenBanner's "Show me how" on
  // a Twilight Navy background) — Dusty Blue is correct here, unchanged,
  // per the audit's two-stop rule: it stays the dark-surface-text stop.
  blue: {
    background: 'transparent',
    color: DUSTY_BLUE,
  },
  // Filled confirm/toggle buttons that aren't the purple primary-action
  // language (e.g. "Set Time") — Dusty Blue (deep), the light-surface fill
  // stop, with Shell White text. See DESIGNSYSTEM.md's Dusty Blue two-stop
  // rule for why this isn't just `tone="blue"` with a solid background.
  blueDeep: {
    background: DUSTY_BLUE_DEEP,
    color: SHELL_WHITE,
  },
  // Secondary/destructive (e.g. "Delete Entry") — an accent-tinted fill
  // instead of a hard outline: the accent stays decoration (a 12% tint +
  // a soft border), the actual readable text is Twilight Navy per Rule 1.
  // Not a raw `outline` + a coral border, which would put the accent
  // color's contrast burden on a 1px line instead of a large fill area.
  sandy: {
    background: alpha('sandy', 0.12),
    color: TWILIGHT_NAVY,
    border: `1px solid ${alpha('sandy', 0.4)}`,
  },
};

/**
 * Shared Button primitive.
 *
 * variant:
 *   fill    — solid background, reproduces AccountScreen's Edit/Save CTAs and
 *             the onboarding/auth Continue buttons
 *   outline — transparent bg + border, reproduces AccountScreen's Cancel
 *   ghost   — translucent glass, dark-modal secondary actions (DESIGNSYSTEM.md
 *             "Ghost Pill Button") — pair with tone="white"
 *   text    — no bg/border, plain link (Cancel/Done, "← Back", "+More")
 *
 * `disabled` uses the app-wide 0.5 opacity convention; `loading` uses 0.6
 * (the AccountScreen Save-button convention) — these are deliberately
 * different states, don't unify them. Callers own the label swap
 * (e.g. children={isSaving ? 'Saving...' : 'Save'}) — Button doesn't
 * hardcode loading copy since it varies per call site.
 *
 * A few one-off patterns (AccountScreen's hover-inverts "Log Out",
 * WelcomeScreen's feedback toggle) don't fit a variant cleanly — use the
 * `style` escape hatch for those rather than growing this API further.
 */
const Button = ({
  variant = 'fill',
  tone = 'purple',
  size = 'md',
  icon: Icon,
  iconSize = 15,
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  children,
  style,
  ...rest
}) => {
  const [hovered, setHovered] = React.useState(false);
  const sizeStyle = SIZE_STYLES[size] || SIZE_STYLES.md;
  const toneStyle = TONE_STYLES[tone] || TONE_STYLES.purple;
  const isInteractionDisabled = disabled || loading;

  let background = toneStyle.background;
  if (variant === 'text') {
    background = 'transparent';
  } else if (hovered && !isInteractionDisabled && toneStyle.hoverBackground) {
    background = toneStyle.hoverBackground;
  }

  const border = variant === 'fill' || variant === 'text' ? 'none' : (toneStyle.border || 'none');
  const color = toneStyle.color;

  return (
    <button
      type="button"
      onClick={isInteractionDisabled ? undefined : onClick}
      disabled={isInteractionDisabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...T.SUBHEADER,
        fontSize: sizeStyle.fontSize,
        fontWeight: variant === 'text' ? 600 : 600,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: fullWidth ? '100%' : undefined,
        padding: variant === 'text' ? '8px 0' : sizeStyle.padding,
        borderRadius: variant === 'ghost' ? '20px' : '8px',
        border,
        background,
        color,
        cursor: isInteractionDisabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : loading ? 0.6 : 1,
        transition: 'background 0.2s',
        ...style,
      }}
      {...rest}
    >
      {Icon && <Icon size={iconSize} color={color} />}
      {children}
    </button>
  );
};

export default Button;
